// Nomad-mon Go! — v11 FORGED FRAMES (DOM). The DOM twin of fusion-rim.js: the
// extravagant animated border a Fusion wears on every DOM surface (Collection
// Fusions tab, forge sheet, unlock ceremony, squad carousel). Per tier band:
//   0  tiers 1-8    EMBER     gold flowing border, ember sparks
//   1  tiers 9-16   PLASMA    violet/cyan border, plasma motes
//   2  tiers 17-24  PRISMATIC full-spectrum border, white-hot filaments
// Every band has the flowing conic border, energy filaments (dashed glowing
// stroke that crawls the perimeter), rising particles, four rotating sigil
// corners and a holo foil sweep over the art. Duos get a lighter treatment: a
// soft pink/cyan rim (the Duo's own effect is its A<->B video cycle). Reduced motion freezes all of it.
// Pure strings + one injected stylesheet; host elements must be position:relative.

export function fusionBandOf(tier) { return tier >= 17 ? 2 : tier >= 9 ? 1 : 0; }

const SIGIL = '<svg viewBox="0 0 40 40" aria-hidden="true"><circle cx="20" cy="20" r="15" fill="none" stroke="currentColor" stroke-width="1.6"/>'
  + '<circle cx="20" cy="20" r="9.5" fill="none" stroke="currentColor" stroke-width="1" stroke-dasharray="3 3"/>'
  + '<path d="M20 3v6M20 31v6M3 20h6M31 20h6M8 8l4 4M28 28l4 4M32 8l-4 4M8 32l4-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>'
  + '<path d="M20 14l3.5 6L20 26l-3.5-6z" fill="currentColor"/></svg>';

export function fusionFrameHtml(tier = 1) {
  const b = fusionBandOf(tier);
  let embers = '';
  for (let i = 0; i < 12; i++) {
    embers += `<i style="--x:${(4 + (i * 37) % 92)}%;--d:${((i * 0.53) % 3.2).toFixed(2)}s;--s:${(0.6 + ((i * 7) % 5) / 6).toFixed(2)}"></i>`;
  }
  return `<span class="fxF b${b}" aria-hidden="true"><span class="fxRing"></span>`
    + '<svg class="fxFil" viewBox="0 0 100 140" preserveAspectRatio="none"><rect x="1.2" y="1.2" width="97.6" height="137.6" rx="7" ry="7"/>'
    + '<rect class="r2" x="1.2" y="1.2" width="97.6" height="137.6" rx="7" ry="7"/></svg>'
    + `<span class="fxFoil"></span><span class="fxEmb">${embers}</span>`
    + `<i class="fxS s0">${SIGIL}</i><i class="fxS s1">${SIGIL}</i><i class="fxS s2">${SIGIL}</i><i class="fxS s3">${SIGIL}</i></span>`;
}
export function duoFrameHtml() {
  // v12b: no diagonal anywhere — the Duo is one card that keeps turning into the other
  return '<span class="dxF" aria-hidden="true"><span class="dxRing"></span></span>';
}

// PERF (v12): no CSS filter, no animated custom property, no animated
// background-position anywhere here — every animation is transform/opacity on
// a small element, so a grid of these costs compositing only. The over-the-top
// look lives in ONE shared WebGL layer (forged-fx.js) on the card in focus.
export const FORGED_FRAME_CSS = `
.fxF{position:absolute;inset:-4px;border-radius:16px;pointer-events:none;z-index:3;--c1:#ff7a1a;--c2:#ffe08a;--c3:#ffb341;--glow:rgba(255,160,60,.5);
  box-shadow:0 0 14px -2px var(--glow),0 0 30px -8px var(--glow)}
.fxF.b1{--c1:#8a5bff;--c2:#3ff0ff;--c3:#d48cff;--glow:rgba(140,110,255,.55)}
.fxF.b2{--c1:#ff5ec8;--c2:#5effe0;--c3:#fff3a0;--glow:rgba(255,255,255,.5)}
.fxRing{position:absolute;inset:0;border-radius:inherit;padding:3px;
  background:conic-gradient(from 20deg,var(--c1),var(--c2),var(--c3),var(--c1),var(--c2),var(--c1));
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;
  mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);mask-composite:exclude}
.fxF.b2 .fxRing{background:conic-gradient(from 20deg,#ff4f8b,#ffb84f,#f8ff6a,#5dff9d,#4fd8ff,#8a6bff,#ff4fd8,#ff4f8b);padding:3.5px}
.fxFil{position:absolute;inset:-2px;width:calc(100% + 4px);height:calc(100% + 4px);overflow:visible;opacity:.8}
.fxFil rect{fill:none;stroke:var(--c2);stroke-width:.9;vector-effect:non-scaling-stroke;stroke-dasharray:2 9 1 14}
.fxFil rect.r2{stroke:var(--c3);stroke-dasharray:1 17 3 8;opacity:.8}
.fxFoil{position:absolute;inset:5px;border-radius:11px;overflow:hidden;mix-blend-mode:color-dodge;opacity:.3}
.fxFoil::before{content:"";position:absolute;inset:-40% -120%;
  background:linear-gradient(115deg,transparent 38%,rgba(255,120,200,.55) 45%,rgba(120,255,240,.55) 50%,rgba(255,250,170,.55) 55%,transparent 62%);
  animation:fxFoil 4.2s ease-in-out infinite;will-change:transform}
@keyframes fxFoil{0%,100%{transform:translateX(-28%)}50%{transform:translateX(28%)}}
.fxEmb{position:absolute;inset:0;overflow:visible}
.fxEmb i{position:absolute;left:var(--x);bottom:-2px;width:5px;height:5px;border-radius:50%;opacity:0;
  background:radial-gradient(circle,#fff 0%,var(--c2) 40%,transparent 72%);transform:scale(var(--s));
  animation:fxEmber 3.2s linear infinite;animation-delay:var(--d)}
.fxEmb i:nth-child(n+7){display:none}
@keyframes fxEmber{0%{opacity:0;transform:translate(0,0) scale(var(--s))}15%{opacity:1}
  100%{opacity:0;transform:translate(calc((var(--s) - 1) * 40px),-120px) scale(calc(var(--s) * .4))}}
.fxS{position:absolute;width:24px;height:24px;color:var(--c2);animation:fxRot 6s linear infinite}
.fxS svg{width:100%;height:100%;display:block}
.fxS.s0{left:-9px;top:-9px}.fxS.s1{right:-9px;top:-9px;animation-direction:reverse}
.fxS.s2{left:-9px;bottom:-9px;animation-direction:reverse}.fxS.s3{right:-9px;bottom:-9px}
@keyframes fxRot{to{transform:rotate(360deg)}}
.dxF{position:absolute;inset:0;border-radius:inherit;pointer-events:none;z-index:3;overflow:hidden}
.dxRing{position:absolute;inset:0;border-radius:inherit;box-shadow:inset 0 0 0 1.5px rgba(255,154,232,.55),inset 0 0 18px rgba(160,120,255,.35)}
.dCyc{position:absolute;inset:0;border-radius:inherit;overflow:hidden;pointer-events:none;z-index:1}
.dCyc img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0;will-change:opacity;border-radius:inherit}
.dCyc .s{-webkit-mask-image:repeating-linear-gradient(90deg,#000 0 3px,transparent 3px 6px);mask-image:repeating-linear-gradient(90deg,#000 0 3px,transparent 3px 6px);
  animation:dcS 8.8s ease-in-out infinite;animation-delay:var(--dd,0s)}
.dCyc .f{animation:dcF 8.8s ease-in-out infinite;animation-delay:var(--dd,0s)}
/* v12c: the baked clip's timing: A 2.4 s, slow 2 s morph, B 2.4 s, morph back */
@keyframes dcS{0%,32%{opacity:0}38.6%{opacity:.16}45%{opacity:0}82%{opacity:0}88.6%{opacity:.16}95%,100%{opacity:0}}
@keyframes dcF{0%,27.3%{opacity:0}50%,77.3%{opacity:1}100%{opacity:0}}
.dLens{position:absolute;inset:0;border-radius:inherit;overflow:hidden;opacity:0;transition:opacity .25s;pointer-events:none;z-index:1}
.dLens.on{opacity:1}
.dLens>video,.dLens>img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block}
.dLens>.b{--lb:0;-webkit-mask-image:repeating-linear-gradient(90deg,#000 0 calc(var(--lb) * 6px),transparent calc(var(--lb) * 6px) 6px);
  mask-image:repeating-linear-gradient(90deg,#000 0 calc(var(--lb) * 6px),transparent calc(var(--lb) * 6px) 6px)}
.dLens>i{position:absolute;inset:-10% -70%;background:linear-gradient(100deg,transparent 40%,rgba(255,255,255,.22) 48%,rgba(190,240,255,.16) 52%,transparent 60%);
  mix-blend-mode:screen;will-change:transform}
.ffv{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:inherit;display:block;opacity:0;transition:opacity .35s}
.ffv.on{opacity:1}
@media (prefers-reduced-motion: reduce){.fxFoil::before,.fxEmb i,.fxS,.dCyc img{animation:none!important}.fxEmb{display:none}}
`;
let injected = false;
export function ensureForgedFrameCss() {
  if (injected || typeof document === 'undefined') return;
  injected = true;
  const st = document.createElement('style');
  st.id = 'forgedFrameCss';
  st.textContent = FORGED_FRAME_CSS;
  document.head.appendChild(st);
}
